import { z } from "zod";
import { DataSourceFigmaImportMetadata, DataSourceFigmaRemote } from "../../data-sources";
import { ImportWarning } from "../warning";
import { FigmaFileAccessData, FigmaFileDownloadScope } from "./figma-files";

//
// Importer payload
//

export const ImportFunctionInput = z.object({
  importJobId: z.string(),
  importContextId: z.string(),
  designSystemId: z.string(),
});

export type ImportFunctionInput = z.infer<typeof ImportFunctionInput>;

//
// Base context
//

export const ImportedFigmaSourceData = z.object({
  sourceId: z.string(),
  figmaRemote: DataSourceFigmaRemote,
});

export const FigmaImportBaseContext = z.object({
  designSystemId: z.string(),

  /**
   * Data required for accessing Figma files. This should contain access data for all file ids
   * mentioned in the `importedSourceDataBySourceId`
   *
   * fileId: file data
   */
  fileAccessByFileId: z.record(FigmaFileAccessData),

  /**
   * Figma source data for which import was requested
   *
   * sourceId: source data
   */
  importedSourceDataBySourceId: z.record(ImportedFigmaSourceData),

  /**
   * Array of warnings that will be written into the import result summary at the end
   * of import job execution and displayed by the client.
   */
  importWarnings: z.record(ImportWarning.array()).default({}),
});

export type ImportedFigmaSourceData = z.infer<typeof ImportedFigmaSourceData>;
export type FigmaImportBaseContext = z.infer<typeof FigmaImportBaseContext>;

//
// Context with source state updates
//

export const FeatureFlagsKeepAliases = z.object({
  isNonCompatibleTypeChangesEnabled: z.boolean().default(false),
  isTypographyUseFontStyleEnabled: z.boolean().default(false),
});
export type FeatureFlagsKeepAliases = z.infer<typeof FeatureFlagsKeepAliases>;

export const FigmaImportContextWithSourcesState = FigmaImportBaseContext.extend({
  sourcesWithMissingAccess: z.array(z.string()).default([]),
  featureFlagsKeepAliases: FeatureFlagsKeepAliases.default({}),
});

export type FigmaImportContextWithSourcesState = z.infer<typeof FigmaImportContextWithSourcesState>;

//
// Context with file download scope
//

export const ChangedImportedFigmaSourceData = ImportedFigmaSourceData.extend({
  importMetadata: DataSourceFigmaImportMetadata,
});

export const FigmaImportContextWithDownloadScopes = FigmaImportContextWithSourcesState.extend({
  /**
   * Describes what to download from each file, this should contain all file id mentioned in
   * importMetadataBySourceId.
   *
   * File id -> file download scope
   */
  fileDownloadScopesByFileId: z.record(FigmaFileDownloadScope),

  /**
   * Sources filtered down to the ones that have changed since last import and therefore need to be
   * imported again.
   *
   * Source id -> import metadata
   */
  changedImportedSourceDataBySourceId: z.record(ChangedImportedFigmaSourceData),
});

export type ChangedImportedFigmaSourceData = z.infer<typeof ChangedImportedFigmaSourceData>;
export type FigmaImportContextWithDownloadScopes = z.infer<typeof FigmaImportContextWithDownloadScopes>;
